import { Link } from "react-router-dom";
import { Trophy, Wallet, Users, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";

const PROBLEMS = [
  "Energy costs eating into bar takings and subscriptions",
  "Endless tariff negotiations every 12-18 months",
  "Capital budgets dominated by clubhouse repairs, not improvement",
  "Pressure on the committee to 'do something green' without funds",
];

const SOLUTIONS = [
  "A fixed kWh rate for 25 years — protect the next generation from wholesale volatility",
  "£0 capital outlay, no impact on cash reserves",
  "Full maintenance, monitoring and insurance built-in",
  "Year 9 exit option keeps optionality with the committee",
];

export default function SportsClubs() {
  return (
    <div data-testid="sports-clubs-page">
      {/* HERO */}
      <section className="relative overflow-hidden navy-panel grain" data-testid="sports-hero">
        <div className="container-seed pt-20 pb-24 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="text-overline text-emerald-400">Sector · Sports Clubs</div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-4 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  Protect club finances<br />for the <span className="text-emerald-400">next generation</span>.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  Committee relief, written into a 25-year agreement. End the annual energy negotiation cycle and
                  redirect every pound saved into the pitch, the bar, and the kids' section.
                </p>
              </FadeUp>
              <FadeUp delay={3}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    data-testid="sports-cta-book"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-4 px-6 rounded-lg group"
                  >
                    Book a Club Assessment
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                  </Link>
                  <Link
                    to="/case-study"
                    data-testid="sports-cta-case"
                    className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 text-white border border-white/15 font-semibold py-4 px-6 rounded-lg"
                  >
                    Read: Chichester RFC
                  </Link>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={2} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={IMAGES.rugbyClub}
                  alt="British rugby clubhouse"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section className="container-seed section-pad" data-testid="sports-problem-solution">
        <div className="max-w-2xl">
          <div className="text-overline">The committee reality</div>
          <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            From volatile bills to <span className="text-emerald-600">committee relief</span>.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <FadeUp>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 h-full">
              <h3 className="font-display font-bold text-slate-900 text-xl">Where most clubs are today</h3>
              <ul className="mt-5 space-y-3">
                {PROBLEMS.map((p) => (
                  <li key={p} className="flex gap-3 text-slate-700 text-sm leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 flex-none" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={1}>
            <div className="bg-slate-900 text-white rounded-xl p-8 h-full">
              <h3 className="font-display font-bold text-white text-xl">Where SEED takes you</h3>
              <ul className="mt-5 space-y-3">
                {SOLUTIONS.map((p) => (
                  <li key={p} className="flex gap-3 text-slate-200 text-sm leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 flex-none" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FOR THE COMMITTEE */}
      <section className="bg-slate-100/60 border-y border-slate-200" data-testid="sports-committee">
        <div className="container-seed section-pad">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Wallet, title: "Predictable accounts", body: "Treasurers can forecast electricity costs to 2050 with confidence — exposure is to inflation only, not to wholesale market spikes." },
              { icon: Trophy, title: "Reinvest in the club", body: "Savings flow back into facilities, junior teams and member experience — not into a utility's margin." },
              { icon: Users, title: "Membership confidence", body: "Communicate genuine ESG progress and long-term financial discipline at every AGM." },
              { icon: ShieldCheck, title: "Contractually protected", body: "Year 9 exit option, transparent agreement and a clear maintenance commitment — so the committee never carries the install or operating risk." },
            ].map((c, i) => (
              <FadeUp key={c.title} delay={i} className={i === 3 ? "md:col-span-3" : ""}>
                <div className="bg-white border border-slate-200 rounded-xl p-7 h-full">
                  <div className="w-11 h-11 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-slate-900 text-lg">{c.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="Sports Club Programme"
        title="Run the numbers for your clubhouse."
        subtitle="Send us a recent bill and your address. Within five working days you'll receive a clear yes/no recommendation and a sample 25-year cost projection."
        ctaLabel="Book Club Assessment"
        testId="sports-cta-section"
      />
    </div>
  );
}
